import { Component, useEffect } from 'react';
import WebViewer from '@pdftron/webviewer';
import styles from './DocumentViewer.scss';
import lodash from 'lodash';
import { getAuthCredentials } from 'common/util/auth-storage';

const FailureComponent = () => (
    <div className={styles.FailureComponent} data-cy="DocumentViewer">
        <div className={styles.FailureComponent__content}>
            <div className={styles.FailureComponent__header}>File Issue</div>
            <div className={styles.FailureComponent__body}>
                <p className={styles.FailureComponent__bold}>
                    Document Preview and 'Send Document For Action' not available because this file cannot be converted.
                </p>
                <ul className={styles.FailureComponent__indent}>
                    <li className={styles.FailureComponent__bold}>If you have not yet filed,</li>
                    <li>please delete and re-upload after taking one or more steps below.</li>
                </ul>
                <ul className={styles.FailureComponent__indent}>
                    <li className={styles.FailureComponent__bold}>If you have already filed,</li>
                    <li>
                        please takes the steps necessary to remove this document from your binder and re-upload after
                        taking one or more of the steps below.
                    </li>
                </ul>
                <p className={styles.FailureComponent__bold}>Options to consider before re-uploading:</p>
                <ul>
                    <li>checking for valid file type (.PDF, .DOC, .XLS, .PPT, etc)</li>
                    <li>converting to a flattened PDF</li>
                    <li>removing password protection</li>
                    <li>ensuring file is not corrupt and can be opened</li>
                    <li>checking for unusual content (embedded videos/ charts/ animations)</li>
                </ul>
                <p className={styles.FailureComponent__bold}>
                    If you have persistent issues, please contact support through the Help tab.
                </p>
            </div>
        </div>
    </div>
);


export class DocumentViewer extends Component {
    render() {
        const { document } = this.props;
        if (lodash.isNil(document)) {
            return null;
        }

        const renditions = lodash.get(document, 'renditions', []);
        const rendition = renditions[0] || null;
        const Viewer = rendition ? DocumentPreview : FailureComponent;
        const { documentUrl, documentType } = getDocumentUrlAndType(document);

        return <Viewer documentUrl={documentUrl} documentType={documentType} />;
    }

    shouldComponentUpdate(nextProps) {
        const { document } = this.props;
        const nextDocument = nextProps.document;
        const documentMetadata = getDocumentUrlAndType(document);
        const nextDocumentMetadata = getDocumentUrlAndType(nextDocument);

        return !lodash.isEqual(documentMetadata, nextDocumentMetadata);
    }
}

export function getDocumentUrlAndType(document) {
    const { renditions } = document;

    const originalRendition = getOriginalRendition(renditions);
    const rendition = renditions[0] || null;
    const documentMetadata = rendition
        ? getDocumentMetadata(rendition)
        : getDocumentMetadata(originalRendition);

    return documentMetadata;
}

const getOriginalRendition = (renditions) => lodash.find(renditions, (r) => r.type === 'ORIGINAL');

function getDocumentMetadata(rendition) {
    const { downloadRequest } = rendition;
    return {
        documentUrl: downloadRequest.tempUrl,
        documentType: getDocumentType(rendition.fileExt),
    };
}

// Document type param, required for the webviewer
function getDocumentType(fileExt) {
    if (fileExt === 'pdf' || fileExt === 'xod') {
        return fileExt;
    }
    return 'office';
}

const DocumentPreview = (props) => {

    useEffect(() => {
        const { documentUrl, documentType } = props;
        if (lodash.isEmpty(documentUrl)) {
            return;
        }

        const { pdftronLicense: obfuscatedPDFTL } = getAuthCredentials();
        const pdftronLicense = window.atob(obfuscatedPDFTL);

        WebViewer(
            {
                licenseKey: pdftronLicense,
                path: '/static/webviewer/10.9.0',
                initialDoc: documentUrl,
                extension: documentType,
                mobileRedirect: false,
                isReadOnly: true,
                enableAzureWorkaround: true,
                disabledElements: [
                    'toggleNotesButton',
                    'toggleCompareModeButton',
                    'markReplaceTextToolButton',
                    'menuButton',
                ]
            },
            document.getElementById('webviewerTarget')
        ).then((instance) => {
            const ui = instance.UI;
            ui.setFitMode(ui.FitMode.FitWidth);
            ui.disableFeatures([
                ui.Feature.Print,
                ui.Feature.Download,
            ]);
        });
    }, []);

    return (
        <div id="webviewerTarget" className={styles.documentPreview}></div>
    );
};

export default DocumentViewer;
